import React, { useEffect, useRef, useState } from 'react';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import './BrowseLoadMore.css';

export const AUTO_LOAD_BATCHES = 3;
export default function BrowseLoadMore({
  query,
  restored = false,
  disabled = false,
  autoDisabled = false,
  autoLoadBatches = AUTO_LOAD_BATCHES,
  preloadDistance = 160,
}) {
  const { locale } = useWebLocale();
  const en = locale === 'en';
  const { fetchNextPage, batchCount } = query;
  const sentinel = useRef(null),
    lock = useRef(false);
  const [awaitInteraction, setAwaitInteraction] = useState(restored);
  useEffect(() => {
    if (!awaitInteraction) return;
    const resume = () => setAwaitInteraction(false);
    // Ignore focus restoration and programmatic scroll; resume only on user input.
    const key = (event) => {
      if (['PageDown', 'ArrowDown', 'End', ' '].includes(event.key)) resume();
    };
    window.addEventListener('wheel', resume, { passive: true });
    window.addEventListener('touchmove', resume, { passive: true });
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('wheel', resume);
      window.removeEventListener('touchmove', resume);
      window.removeEventListener('keydown', key);
    };
  }, [awaitInteraction]);
  const load = () => {
    if (disabled || query.busy || !query.hasNextPage || lock.current) return;
    lock.current = true;
    void fetchNextPage({ cancelRefetch: false }).finally(() => {
      lock.current = false;
    });
  };
  const automatic =
    !disabled &&
    !autoDisabled &&
    !awaitInteraction &&
    !query.busy &&
    !query.isError &&
    !query.isFetchNextPageError &&
    query.batchCount > 0 &&
    query.batchCount <= autoLoadBatches &&
    query.hasNextPage;
  useEffect(() => {
    if (!automatic || !sentinel.current || typeof IntersectionObserver === 'undefined') return;
    let alive = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (alive && entries.some((entry) => entry.isIntersecting) && !lock.current) {
          lock.current = true;
          void fetchNextPage({ cancelRefetch: false }).finally(() => {
            lock.current = false;
          });
        }
      },
      { rootMargin: `0px 0px ${preloadDistance}px 0px` },
    );
    observer.observe(sentinel.current);
    return () => {
      alive = false;
      observer.disconnect();
    };
  }, [automatic, fetchNextPage, batchCount, preloadDistance]);
  if (!query.data || (!query.hasNextPage && !query.isFetchNextPageError)) return null;
  return (
    <div className="pp-browse-load-more">
      <span ref={sentinel} className="pp-browse-load-sentinel" aria-hidden="true" />
      {query.isFetchNextPageError && (
        <p role="alert">
          {en
            ? 'Could not load more. Your list is still here.'
            : '더 불러오지 못했어요. 현재 목록은 그대로 유지됩니다.'}
        </p>
      )}
      <button
        type="button"
        className="pp-action pp-playlist-more"
        disabled={disabled || query.busy}
        onClick={load}
      >
        {query.isFetchingNextPage ? (
          <span role="status">{en ? 'Loading…' : '불러오는 중…'}</span>
        ) : query.isFetchNextPageError ? (
          en ? (
            'Retry'
          ) : (
            '다시 시도'
          )
        ) : en ? (
          'Show more'
        ) : (
          '더보기'
        )}
      </button>
    </div>
  );
}
